import React from 'react'
import { MeetingSummaryElementProps, ParticipantsElement } from '../../../models'
import { ROLES_PER_PAGE, useMeetingUsers, UsersConsent } from '../../../../hooks/users'
import { getUserDisplayName } from '../../../../../common/aifeatures/utils'
import { CONSENT_EXCLUDED_ROLES } from '../../../../ConfigureAssignment/AssignmentRoleNameMapping'
import { Col, Container, Row } from 'react-bootstrap'
import { ConsentIcon } from '../../../../../../../../components/Consent/ConsentIcon'
import { useAppSelector } from '../../../../../../../../store/hooks'
import { SpeakingDistributionIcon } from '../../../../ConfigureAssignment/SpeakingDistributionIcon'
import { selectSafeAuthenticatedUser } from '../../../../../../../../store/auth/authSlice'
import { Button } from '../../../../../../../../components/Button/Button'
import { User } from '../../../../../../../../models/User'
import { selectRoomSession } from '../../../../../../../../store/meetingSummary/roomSession'
import './EmbeddedComponents.scss'
import { getManualParticipantDisplayReason } from '../../../../ConfigureAssignment/utils'

export const EmbeddedParticipants: React.FC<MeetingSummaryElementProps<ParticipantsElement>> = () => {
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const roomSession = useAppSelector(selectRoomSession)
  const {
    usersByRoles,
    usersByRolesOnCurrentPage,
    usersConsent,
    getSpeakingPercentage,
    pages,
    currentPage,
    nextPage,
    previousPage,
    manualParticipantsByUserId,
    manualParticipantAddedByNames,
  } = useMeetingUsers(roomSession)

  const getParticipantsBody = (usersByRoles: { [key: string]: User[] }, usersConsent: UsersConsent | null) => {
    return (
      <>
        {Object.entries(usersByRoles).map(([role, users]) => {
          return (
            <Col key={role} xs={12} md={12 / ROLES_PER_PAGE} className='d-flex flex-column p-0'>
              <div className='py-2 lead-extra-small'>
                {role} ({users.length})
              </div>
              <div className='d-flex flex-column gap-2 participants-container p-2'>
                {users.map((user) => {
                  const consent = usersConsent ? usersConsent[user.id] : null
                  const userType = currentUserId === user.id ? 'current' : (user.type ?? 'anonymous')
                  const maybeManualParticipant = user.id in manualParticipantsByUserId ? manualParticipantsByUserId[user.id] : null
                  return (
                    <div key={user.id}>
                      <div className='d-flex gap-2 align-items-center'>
                        <div className='paragraph-medium medium'>{getUserDisplayName(user)}</div>
                        <SpeakingDistributionIcon userType={userType} percentage={getSpeakingPercentage(user)} />
                        {usersConsent
                          ? user.roles.every((role) => !CONSENT_EXCLUDED_ROLES.includes(role.id)) && (
                              <Col className='p-0 d-flex align-items-center gap-2' xs='auto'>
                                <ConsentIcon contact={user} consent={consent} showText={true} />
                              </Col>
                            )
                          : null}
                      </div>
                      {maybeManualParticipant ? (
                        <div className={'d-flex flex-column pt-1 gap-1'}>
                          <div className='paragraph-medium flex-row'>
                            Added by {manualParticipantAddedByNames[maybeManualParticipant.addedByUserId] ?? maybeManualParticipant.addedByUserId}
                          </div>
                          <div className='paragraph-medium fst-italic flex-row'>Reason: {getManualParticipantDisplayReason(maybeManualParticipant, false)}</div>
                        </div>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            </Col>
          )
        })}
      </>
    )
  }

  return (
    <Container className='zocks-pl-2.75 my-3 d-flex flex-column zocks-gap-2 w-100 mw-100 embedded-container'>
      <Row className='mt-3 d-none d-lg-flex gap-2'>{getParticipantsBody(usersByRolesOnCurrentPage, usersConsent)}</Row>
      <Row className='mt-3 d-flex d-lg-none'>{getParticipantsBody(usersByRoles, usersConsent)}</Row>
      {pages > 1 ? (
        <Row className='d-none d-lg-flex justify-content-end'>
          <Col className='p-0 d-flex zocks-gap-0.75 align-items-center' xs='auto'>
            <Button variant='link' className='page-button' disabled={currentPage === 1} onClick={previousPage}>
              <i className='bi bi-chevron-left' />
            </Button>
            <div className='lead-extra-small'>
              {currentPage}/{pages}
            </div>
            <Button variant='link' className='page-button' disabled={currentPage === pages} onClick={nextPage}>
              <i className='bi bi-chevron-right' />
            </Button>
          </Col>
        </Row>
      ) : null}
    </Container>
  )
}
